import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
    Search, MapPin, Building2, X, Loader2, Sparkles,
    ArrowRight, Hotel, Users, Crown
} from 'lucide-react';
import GoogleMap from '@/components/map/GoogleMap';
import { COMPSET_PERMISSION_MAPPER, ERROR_CODES, MAX_COMPSET_NAME_LENGTH, PLAN_NAME_MAPPER } from '@/constants';
import { useCompset } from '@/contexts/CompsetContext';
import { apiClient } from '@/services/apiClient';
import { useQueryClient } from '@tanstack/react-query';
import { API_URLS, IN_APP_ROUTES } from '@/constants/urls';
import { queryKeys } from '@/services/queryKeyFactory';
import { formatDateYMD } from '@/helpers/formatters';
import { useAuth } from '@/contexts/AuthContext';
import { usePlans } from '@/contexts/PlanRulesContext';
import { apiErrorHandler } from '@/helpers/apiErrorHandler';
import SettingUpCompset from './modals/SettingUpCompset';

function Compset({ mode = "create", competitorHotels = [], clientHotel = null }) {

    const [isSaving, setIsSaving] = useState(false);
    const [isUpdating, setIsUpdating] = useState(false);

    const [setupStep, setSetupStep] = useState(0);
    const [searchMode, setSearchMode] = useState('your_hotel');

    const { runCompset, editingCompset, setEditingCompset } = useCompset();
    const { profileData: userInfo, isLoadingProfileData } = useAuth();
    const { planRules, isLoadingPlanRules, maxCompetitorsPerCompset } = usePlans();

    const queryClient = useQueryClient();
    const navigate = useNavigate();

    const compsetPermissions = COMPSET_PERMISSION_MAPPER[mode];
    const isEditMode = mode === 'edit';

    // Pre-fill name from context when editing; editable on create/update
    const [compsetName, setCompsetName] = useState(
        isEditMode ? (editingCompset?.compset_name ?? '') : ''
    );

    const [yourHotel, setYourHotel] = useState(clientHotel);

    // competitorHotels from context already have removable: false on existing ones
    const [competitors, setCompetitors] = useState(competitorHotels);

    const handleRemoveCompetitor = (index) => {
        if (!competitors[index]?.removable) return; // guard: never remove locked ones
        setCompetitors(prev => prev.filter((_, i) => i !== index));
        toast.info('Competitor removed');
    };

    const handleClearYourHotel = () => {
        if (!yourHotel?.removable) return;
        setYourHotel(null);
        toast.info('Your hotel cleared');
    };

    const handleUnavailableHotels = (hotels) => {
        if (hotels?.length > 0) {
            const place_ids = hotels.map(h => h?.placeID);
            setCompetitors(prev =>
                prev.map(comp =>
                    place_ids.includes(comp?.placeId)
                        ? { ...comp, isUnavilable: true }
                        : comp
                )
            );
        }
    };

    // ── Validation (shared) ──────────────────────────────────────
    const validateBeforeSave = () => {
        if (!isEditMode && !compsetName.trim()) {
            toast.error('Please enter a name for your compset');
            return false;
        }
        if (!yourHotel) {
            toast.error('Please select your hotel first');
            return false;
        }
        if (competitors.length === 0) {
            toast.error('Please add at least one competitor');
            return false;
        }
        if (isEditMode) {
            const hasNew = competitors.some(c => c.removable);
            if (!hasNew) {
                toast.error('Please add at least one new competitor');
                return false;
            }
        }
        return true;
    };

    // ── CREATE ───────────────────────────────────────────────────
    const isRegularPlan = planRules?.plan_info?.plan_name == PLAN_NAME_MAPPER.regular
    const handleCreate = async () => {
        setIsSaving(true);
        setSetupStep(0);
        let timer1, timer2, timer3;


        try {
            const result = await apiClient.post(API_URLS.CREATE_COMPSET, {
                data: {
                    rateshopName: compsetName,
                    client: {
                        hotelName: yourHotel.hotelName,
                        geometry: yourHotel.geometry,
                        placeId: yourHotel.placeId,
                        hotelAddress: yourHotel.hotelAddress,
                        addressComponents: yourHotel.addressComponents,
                    },
                    competitors: competitors.map(hotel => ({
                        hotelName: hotel.hotelName,
                        geometry: hotel.geometry,
                        placeId: hotel.placeId,
                        hotelAddress: hotel.hotelAddress,
                        addressComponents: hotel.addressComponents,
                    })),
                    mapUrl: null,
                }
            });

            if (result.status === 200) {
                setSetupStep(1);
                toast.success('Compset saved successfully!');
                timer1 = setTimeout(() => setSetupStep(2), 4000);
                timer2 = setTimeout(() => setSetupStep(3), 8000);

                if (!isRegularPlan) {
                    timer3 = setTimeout(() => setSetupStep(4), 10000);
                }

                const isOk = await runCompset(
                    formatDateYMD(new Date()),
                    result.data?.rateshopId,
                    result.data?.rateshopName
                );

                await Promise.all(
                    [queryKeys.me(), queryKeys.compsets()].map(key =>
                        queryClient.invalidateQueries({ queryKey: key })
                    )
                );

                if (isOk) navigate(IN_APP_ROUTES.DASHBOARD);
            }
        } catch (error) {
            const errorData = error?.response?.data;
            if (errorData?.errorCode === ERROR_CODES.COMPSET_COMPETITOR_NOT_FOUND) {
                handleUnavailableHotels(errorData?.unavailable_hotels);
            }
            apiErrorHandler(error);
        } finally {
            setSetupStep(0);
            setIsSaving(false);
            clearTimeout(timer1);
            clearTimeout(timer2);
            clearTimeout(timer3);
        }
    };

    // ── EDIT ─────────────────────────────────────────────────────
    const handleEdit = async () => {
        setIsUpdating(true);
        // setSetupStep(0);
        // let timer1, timer2;

        // Only the newly added competitors (removable: true) are sent to backend
        const newCompetitors = competitors.filter(c => c.removable);

        const data = {
            compset_id: editingCompset?.compset_id,
            compset_name: editingCompset?.compset_name,
            competitors: newCompetitors.map(hotel => ({
                hotelName: hotel.hotelName,
                geometry: hotel.geometry,
                placeId: hotel.placeId,
                hotelAddress: hotel.hotelAddress,
                addressComponents: hotel.addressComponents,
            })),
        }

        // return

        try {
            // TODO: swap mock with real call once backend is ready
            const result = await apiClient.post(API_URLS.EDIT_COMPSET, data);



            if (result.status === 200) {
                // setSetupStep(1);
                toast.success('Compset saved successfully!');
                // timer1 = setTimeout(() => setSetupStep(2), 4000);
                // timer2 = setTimeout(() => setSetupStep(3), 8000);

                await Promise.all(
                    [queryKeys.me()].map(key =>
                        queryClient.invalidateQueries({ queryKey: key })
                    )
                );

                navigate(IN_APP_ROUTES.DASHBOARD);

                await Promise.all(
                    [queryKeys.compsets()].map(key =>
                        queryClient.invalidateQueries({ queryKey: key })
                    )
                );
                setEditingCompset(null); // clear editing state
            }


        } catch (error) {
            const errorData = error?.response?.data;
            if (errorData?.errorCode === ERROR_CODES.COMPSET_COMPETITOR_NOT_FOUND) {
                handleUnavailableHotels(errorData?.unavailable_hotels);
            }
            apiErrorHandler(error);
        } finally {
            // setSetupStep(0);
            setIsUpdating(false);
            // clearTimeout(timer1);
            // clearTimeout(timer2);
        }
    };

    const handleSave = () => {
        if (!validateBeforeSave()) return;
        isEditMode ? handleEdit() : handleCreate();
    };

    if (isLoadingProfileData || isLoadingPlanRules) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center">
                <div className="text-center">
                    <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-4" />
                    <p className="text-muted-foreground">Loading...</p>
                </div>
            </div>
        );
    }

    const userPlan = planRules?.plan_info;
    const MAX_COMPETITORS = maxCompetitorsPerCompset;

    return (
        <div className="min-h-screen bg-background" data-testid="compset-setup-page">

            <SettingUpCompset
                isSaving={isSaving}
                setupStep={setupStep}
                maxStep={!isRegularPlan ? 5 : 4}
            />

            {/* Background Effects */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-primary/5 rounded-full blur-3xl" />
                <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-accent/5 rounded-full blur-3xl" />
            </div>

            <div className="relative z-10 max-w-7xl mx-auto p-6">
                {/* Header */}
                <div className="mb-8 text-center">
                    <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary mb-4">
                        <Sparkles className="h-4 w-4" />
                        <span className="text-sm font-medium">
                            {isEditMode ? 'Edit Your Competitive Set' : 'Setup Your Competitive Set'}
                        </span>
                    </div>
                    <h1 className="text-3xl font-bold text-foreground mb-2">
                        {isEditMode ? 'Add Competitors' : 'Configure Your Hotels'}
                    </h1>
                    <p className="text-muted-foreground max-w-2xl mx-auto">
                        {isEditMode
                            ? 'Your existing hotels are locked. You can add new competitors below.'
                            : 'Search and select your hotel, then add your competitors to start tracking market intelligence.'
                        }
                    </p>

                    {/* Plan indicator */}
                    <div className="flex items-center justify-center gap-2 mt-4">
                        <Badge variant={userPlan?.plan_name === PLAN_NAME_MAPPER.trial ? 'secondary' : 'default'} className="gap-1">
                            {userPlan?.plan_name === PLAN_NAME_MAPPER.advance && <Crown className="h-3 w-3" />}
                            {userPlan?.plan_name?.charAt(0).toUpperCase() + userPlan?.plan_name?.slice(1)} Plan
                        </Badge>
                        <span className="text-sm text-muted-foreground">
                            • Up to {MAX_COMPETITORS} competitors
                        </span>
                    </div>
                </div>

                {/* Main Content */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Left Panel */}
                    <div className="space-y-6">

                        {/* Compset Name */}
                        <Card className="border-primary/30">
                            <CardHeader className="pb-4">
                                <CardTitle className="text-lg flex items-center gap-2">
                                    <Sparkles className="h-5 w-5 text-primary" />
                                    Compset Name
                                </CardTitle>
                                <CardDescription>
                                    {isEditMode
                                        ? 'Compset name cannot be changed'
                                        : 'Give your competitive set a memorable name'
                                    }
                                </CardDescription>
                            </CardHeader>
                            <CardContent>
                                <Input
                                    placeholder="e.g., Downtown NYC Hotels, Beach Resort Market..."
                                    value={compsetName}
                                    onChange={(e) => {
                                        if (!compsetPermissions?.canEditCompsetName) return;
                                        if (e.target.value.length > MAX_COMPSET_NAME_LENGTH) return;
                                        setCompsetName(e.target.value);
                                    }}
                                    className={`text-lg ${isEditMode ? 'opacity-60 cursor-not-allowed bg-muted' : ''}`}
                                    disabled={!compsetPermissions?.canEditCompsetName}
                                    data-testid="compset-name-input"
                                />
                                {!isEditMode && (
                                    <>
                                        <p className='text-xs text-gray-500 text-end mr-2 mb-1'>
                                            {`${compsetName.length} / ${MAX_COMPSET_NAME_LENGTH}`}
                                        </p>
                                        <p className="text-xs text-muted-foreground mt-2">
                                            This name will be displayed in your dashboard header
                                        </p>
                                    </>
                                )}
                            </CardContent>
                        </Card>

                        {/* Search — in edit mode, only competitor search is available */}
                        <Card>
                            <CardHeader className="pb-4">
                                <CardTitle className="text-lg flex items-center gap-2">
                                    <Search className="h-5 w-5" />
                                    Search Hotels
                                </CardTitle>
                                <CardDescription>
                                    {isEditMode
                                        ? 'Search for new competitors to add'
                                        : 'Search for hotels using Google Maps'
                                    }
                                </CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                {/* Mode Toggle — hidden in edit mode (competitor search only) */}
                                {!isEditMode && (
                                    <div className="flex gap-2">
                                        <Button
                                            variant={searchMode === 'your_hotel' ? 'default' : 'outline'}
                                            size="sm"
                                            onClick={() => setSearchMode('your_hotel')}
                                            disabled={!!yourHotel}
                                            className="flex-1"
                                        >
                                            <Hotel className="h-4 w-4 mr-2" />
                                            Your Hotel
                                        </Button>
                                        <Button
                                            variant={searchMode === 'competitor' ? 'default' : 'outline'}
                                            size="sm"
                                            onClick={() => setSearchMode('competitor')}
                                            disabled={!yourHotel}
                                            className="flex-1"
                                        >
                                            <Users className="h-4 w-4 mr-2" />
                                            Add Competitor
                                        </Button>
                                    </div>
                                )}

                                <GoogleMap
                                    searchMode={isEditMode ? 'competitor' : searchMode}
                                    setSearchMode={setSearchMode}
                                    clientHotel={{ yourHotel, setYourHotel }}
                                    competitorsHotel={{
                                        competitors,
                                        // Wrap setCompetitors to ensure new hotels get removable: true
                                        setCompetitors: (updater) => {
                                            setCompetitors(prev => {
                                                const next = typeof updater === 'function' ? updater(prev) : updater;
                                                // Any hotel added beyond what was there originally gets removable: true
                                                return next.map((hotel, i) => ({
                                                    ...hotel,
                                                    removable: hotel.removable ?? true,
                                                }));
                                            });
                                        }
                                    }}
                                />
                            </CardContent>
                        </Card>
                    </div>

                    {/* Right Panel */}
                    <div className="space-y-6">

                        {/* Your Hotel */}
                        <Card className={yourHotel ? 'border-primary/50' : ''}>
                            <CardHeader className="pb-4">
                                <div className='flex justify-between'>
                                    <CardTitle className="text-lg flex items-center gap-2">
                                        <Hotel className="h-5 w-5 text-primary" />
                                        Your Hotel
                                        {isEditMode && (
                                            <Badge variant="secondary" className="text-xs ml-1">Locked</Badge>
                                        )}
                                    </CardTitle>
                                    {isEditMode && (
                                        <a
                                            href="mailto:support@prowentech.com?subject=Update Hotel Request"
                                            className="text-blue-700 text-xs cursor-pointer hover:underline"
                                        >
                                            Need to update your hotel?
                                        </a>
                                    )}
                                </div>
                                <CardDescription>
                                    {isEditMode
                                        ? 'Your hotel cannot be changed while editing'
                                        : 'This is the hotel you own or manage'
                                    }
                                </CardDescription>
                            </CardHeader>
                            <CardContent>
                                {yourHotel ? (
                                    <div className="p-4 bg-primary/5 border border-primary/20 rounded-lg">
                                        <div className="flex items-start justify-between gap-4">
                                            <div className="flex items-start gap-3 flex-1 min-w-0">
                                                <div className="p-2 rounded-lg bg-primary/10 shrink-0">
                                                    <Building2 className="h-5 w-5 text-primary" />
                                                </div>
                                                <div className="min-w-0">
                                                    <p className="font-semibold text-foreground">{yourHotel?.hotelName ?? yourHotel?.name}</p>
                                                    <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                                                        <MapPin className="h-3 w-3 shrink-0" />
                                                        <span className="truncate">{yourHotel?.hotelAddress ?? yourHotel?.address}</span>
                                                    </p>
                                                </div>
                                            </div>
                                            {yourHotel?.removable && (
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    onClick={handleClearYourHotel}
                                                    className="shrink-0 text-muted-foreground hover:text-destructive"
                                                >
                                                    <X className="h-4 w-4" />
                                                </Button>
                                            )}
                                        </div>
                                    </div>
                                ) : (
                                    <div className="p-8 border-2 border-dashed border-border rounded-lg text-center">
                                        <Hotel className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                                        <p className="text-muted-foreground">Search and select your hotel</p>
                                    </div>
                                )}
                            </CardContent>
                        </Card>

                        {/* Competitors */}
                        <Card>
                            <CardHeader className="pb-4">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <CardTitle className="text-lg flex items-center gap-2">
                                            <Users className="h-5 w-5" />
                                            Competitors
                                        </CardTitle>
                                        <CardDescription>
                                            Hotels you want to track ({competitors.length}/{MAX_COMPETITORS})
                                        </CardDescription>
                                    </div>
                                    <Badge variant={competitors.length >= MAX_COMPETITORS ? 'secondary' : 'outline'}>
                                        {competitors.length}/{MAX_COMPETITORS}
                                    </Badge>
                                </div>
                            </CardHeader>
                            <CardContent>
                                {competitors.length > 0 ? (
                                    <div className="space-y-3">
                                        {competitors.map((competitor, index) => (
                                            <div
                                                key={competitor?.placeId || competitor?.google_place_id || index}
                                                className={`p-4 border rounded-lg transition-colors ${competitor?.isUnavilable
                                                    ? 'bg-red-100/55 border-red-400 hover:border-red-600'
                                                    : !competitor?.removable
                                                        ? 'bg-muted/40 border-border'   // locked = muted style
                                                        : 'border-border hover:border-primary/30'
                                                    }`}
                                                data-testid={`competitor-${index}`}
                                            >
                                                <div className="flex items-start justify-between gap-4">
                                                    <div className="flex items-start gap-3 flex-1 min-w-0">
                                                        <div className="p-2 rounded-lg bg-muted shrink-0">
                                                            <Building2 className="h-4 w-4 text-muted-foreground" />
                                                        </div>
                                                        <div className="min-w-0">
                                                            <div className="flex items-center gap-2">
                                                                <p className="font-medium text-foreground">
                                                                    {competitor?.hotelName ?? competitor?.name}
                                                                </p>
                                                                {/* Visual indicator for locked existing competitors */}
                                                                {isEditMode && !competitor?.removable && (
                                                                    <Badge variant="secondary" className="text-xs shrink-0">Existing</Badge>
                                                                )}
                                                            </div>
                                                            <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                                                                <MapPin className="h-3 w-3 shrink-0" />
                                                                <span className="truncate">
                                                                    {competitor?.hotelAddress ?? competitor?.address}
                                                                </span>
                                                            </p>
                                                        </div>
                                                    </div>
                                                    {competitor?.removable && (
                                                        <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            onClick={() => handleRemoveCompetitor(index)}
                                                            className="shrink-0 text-muted-foreground hover:text-destructive"
                                                        >
                                                            <X className="h-4 w-4" />
                                                        </Button>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="p-8 border-2 border-dashed border-border rounded-lg text-center">
                                        <Users className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                                        <p className="text-muted-foreground">
                                            {yourHotel ? 'Search and add competitor hotels' : 'Select your hotel first'}
                                        </p>
                                    </div>
                                )}

                                {yourHotel && competitors.length < MAX_COMPETITORS && competitors.length > 0 && (
                                    <p className="text-sm text-muted-foreground text-center mt-4">
                                        You can add {MAX_COMPETITORS - competitors.length} more competitor{MAX_COMPETITORS - competitors.length > 1 ? 's' : ''}
                                    </p>
                                )}
                            </CardContent>
                        </Card>

                        {/* Actions */}
                        <div className="flex gap-4">
                            {compsetPermissions?.showCancelButton && (
                                <Button
                                    className="flex-1"
                                    variant="outline"
                                    onClick={() => navigate(IN_APP_ROUTES.DASHBOARD)}
                                    disabled={isSaving || isUpdating}
                                >
                                    Cancel
                                </Button>
                            )}
                            <Button
                                onClick={handleSave}
                                disabled={!yourHotel || competitors.length === 0 || isSaving || isUpdating}
                                className="flex-1 gap-2"
                                data-testid="save-compset-btn"
                            >
                                {isSaving || isUpdating ? (
                                    <>
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                        {isEditMode ? 'Updating...' : 'Setting Up...'}
                                    </>
                                ) : (
                                    <>
                                        {isEditMode ? 'Save Changes' : 'Continue to Dashboard'}
                                        <ArrowRight className="h-4 w-4" />
                                    </>
                                )}
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default Compset;